<template>
  <div>
    <!-- Main Table Tabs -->
    <div class="tabs">
      <button
        class="tab"
        :class="{ 'tab-active': mainActiveTab === 'farmDetails' }"
        @click="mainActiveTab = 'farmDetails'"
        type="button"
      >
        Farm Details
      </button>
      <button
        class="tab"
        :class="{ 'tab-active': mainActiveTab === 'applicationDetails' }"
        @click="mainActiveTab = 'applicationDetails'"
        type="button"
      >
        Application Details
      </button>
      <button
        class="tab"
        :class="{ 'tab-active': mainActiveTab === 'transportMedia' }"
        @click="mainActiveTab = 'transportMedia'"
        type="button"
      >
        Transport & Media
      </button>
    </div>

    <div class="table-container">
      <!-- Farm Details Table -->
      <b-table
        v-show="mainActiveTab === 'farmDetails'"
        striped
        hover
        :items="items"
        :fields="farmDetailsFields"
        :per-page="10"
        :current-page="currentPage"
        id="biochar-farm-table"
        responsive
      >
        <template v-slot:cell(actions)="data">
          <button
            class="btn btn-info btn-sm dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Action
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <b-button
              v-b-modal.modal-prevent-closing
              @click="openEditModal(data.item.id)"
              class="dropdown-item"
            >
              <i class="fa fa-edit"></i>
              Edit
            </b-button>
            <button
              class="dropdown-item"
              @click="showDeletePrompt(data.item.id)"
            >
              <i class="fa fa-trash text-danger"></i>
              Delete
            </button>
          </div>
        </template>
      </b-table>

      <!-- Application Details Table -->
      <b-table
        v-show="mainActiveTab === 'applicationDetails'"
        striped
        hover
        :items="items"
        :fields="applicationDetailsFields"
        :per-page="10"
        :current-page="currentPage"
        id="biochar-application-table"
        responsive
      >
        <template v-slot:cell(actions)="data">
          <button
            class="btn btn-info btn-sm dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Action
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <b-button
              v-b-modal.modal-prevent-closing
              @click="openEditModal(data.item.id)"
              class="dropdown-item"
            >
              <i class="fa fa-edit"></i>
              Edit
            </b-button>
            <button
              class="dropdown-item"
              @click="showDeletePrompt(data.item.id)"
            >
              <i class="fa fa-trash text-danger"></i>
              Delete
            </button>
          </div>
        </template>
      </b-table>

      <!-- Transport & Media Table -->
      <b-table
        v-show="mainActiveTab === 'transportMedia'"
        striped
        hover
        :items="items"
        :fields="transportMediaFields"
        :per-page="10"
        :current-page="currentPage"
        id="biochar-transport-table"
        responsive
      >
        <template v-slot:cell(imageUrls)="data">
          <div v-if="data.item.imageUrls && data.item.imageUrls.length">
            <a
              v-for="(url, index) in data.item.imageUrls"
              :key="`url-${index}`"
              :href="url"
              target="_blank"
              class="image-url-link"
              :title="url"
            >
              {{ truncateUrl(url)
              }}<span v-if="index < data.item.imageUrls.length - 1">, </span>
            </a>
          </div>
          <span v-else>-</span>
        </template>
        <template v-slot:cell(actions)="data">
          <button
            class="btn btn-info btn-sm dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Action
          </button>
          <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <b-button
              v-b-modal.modal-prevent-closing
              @click="openEditModal(data.item.id)"
              class="dropdown-item"
            >
              <i class="fa fa-edit"></i>
              Edit
            </b-button>
            <button
              class="dropdown-item"
              @click="showDeletePrompt(data.item.id)"
            >
              <i class="fa fa-trash text-danger"></i>
              Delete
            </button>
          </div>
        </template>
      </b-table>
    </div>

    <b-pagination
      v-model="currentPage"
      :total-rows="rows"
      :per-page="10"
      :aria-controls="getAriaControlsId()"
    ></b-pagination>
    <p class="mt-3">Current Page: {{ currentPage }}</p>

    <!-- Edit Modal (same as original) -->
    <b-modal
      id="modal-prevent-closing"
      ref="modal"
      title="Edit Biochar Application"
      hide-footer
      @hide="resetEditBiocharApplication"
    >
      <div class="tabs">
        <button
          class="tab"
          :class="{ 'tab-active': modalActiveTab === 'farmDetails' }"
          @click="modalActiveTab = 'farmDetails'"
          type="button"
        >
          Farm Details
        </button>
        <button
          class="tab"
          :class="{ 'tab-active': modalActiveTab === 'applicationDetails' }"
          @click="modalActiveTab = 'applicationDetails'"
          type="button"
        >
          Application Details
        </button>
        <button
          class="tab"
          :class="{ 'tab-active': modalActiveTab === 'transportMedia' }"
          @click="modalActiveTab = 'transportMedia'"
          type="button"
        >
          Transport & Media
        </button>
      </div>

      <form @submit.stop.prevent="updateBiocharApplication">
        <!-- Farm Details Tab -->
        <div v-show="modalActiveTab === 'farmDetails'" class="tab-content">
          <b-form-group label="Owner Name" label-for="ownerName-input">
            <b-form-input
              id="ownerName-input"
              v-model="editBiocharApplication.ownerName"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Farm Size (ha)" label-for="farmSize-input">
            <b-form-input
              id="farmSize-input"
              type="number"
              step="any"
              v-model="editBiocharApplication.farmSize"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Crop" label-for="crop-input">
            <b-form-input
              id="crop-input"
              v-model="editBiocharApplication.crop"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Officer Name" label-for="officerName-input">
            <b-form-input
              id="officerName-input"
              v-model="editBiocharApplication.officerName"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Officer Email" label-for="officerEmail-input">
            <b-form-input
              id="officerEmail-input"
              v-model="editBiocharApplication.officerEmail"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Contact Info" label-for="contactInfo-input">
            <b-form-input
              id="contactInfo-input"
              v-model="editBiocharApplication.contactInfo"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Latitude" label-for="latitude-input">
            <b-form-input
              id="latitude-input"
              type="number"
              step="any"
              v-model="editBiocharApplication.latitude"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Longitude" label-for="longitude-input">
            <b-form-input
              id="longitude-input"
              type="number"
              step="any"
              v-model="editBiocharApplication.longitude"
              required
            ></b-form-input>
          </b-form-group>
          <div class="btn-container">
            <b-button
              type="submit"
              variant="primary"
              :disabled="isFarmDetailsInvalid"
              @click="modalActiveTab = 'farmDetails'"
            >
              Update Farm Details
            </b-button>
          </div>
        </div>

        <!-- Application Details Tab -->
        <div
          v-show="modalActiveTab === 'applicationDetails'"
          class="tab-content"
        >
          <b-form-group label="Start Date" label-for="startDate-input">
            <b-form-input
              id="startDate-input"
              type="date"
              v-model="editBiocharApplication.startDate"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="End Date" label-for="endDate-input">
            <b-form-input
              id="endDate-input"
              type="date"
              v-model="editBiocharApplication.endDate"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group
            label="Amount per Spot (kg)"
            label-for="amountPerSpot-input"
          >
            <b-form-input
              id="amountPerSpot-input"
              type="number"
              step="any"
              v-model="editBiocharApplication.amountPerSpot"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group label="Number of Spots" label-for="numberOfSpots-input">
            <b-form-input
              id="numberOfSpots-input"
              type="number"
              v-model="editBiocharApplication.numberOfSpots"
              required
            ></b-form-input>
          </b-form-group>
          <b-form-group
            label="Amount of Biochar (kg)"
            label-for="amountOfBiochar-input"
          >
            <b-form-input
              id="amountOfBiochar-input"
              type="number"
              step="any"
              v-model="editBiocharApplication.amountOfBiochar"
              required
            ></b-form-input>
          </b-form-group>
          <div class="btn-container">
            <b-button
              type="submit"
              variant="primary"
              :disabled="isApplicationDetailsInvalid"
              @click="modalActiveTab = 'applicationDetails'"
            >
              Update Application Details
            </b-button>
          </div>
        </div>

        <!-- Transport & Media Tab -->
        <div v-show="modalActiveTab === 'transportMedia'" class="tab-content">
          <b-form-group
            label="Transport Vehicle Type"
            label-for="transportVehicleType-input"
          >
            <b-form-select
              id="transportVehicleType-input"
              v-model="editBiocharApplication.transportVehicleType"
              :options="transportOptions"
              required
            ></b-form-select>
          </b-form-group>
          <b-form-group label="Image URLs" label-for="imageUrls-input">
            <div
              v-for="(url, index) in editBiocharApplication.imageUrls"
              :key="`url-${index}`"
              class="mb-2 url-input-row"
            >
              <b-form-input
                v-model="editBiocharApplication.imageUrls[index]"
                placeholder="Enter Google Drive Image URL"
              ></b-form-input>
              <b-button
                v-if="index > 0"
                variant="danger"
                size="sm"
                class="mt-1 cancel-btn"
                @click="removeImageUrl(index)"
              >
                ×
              </b-button>
            </div>
            <b-button
              variant="primary"
              size="sm"
              class="action-btn add-url-btn"
              @click="addImageUrl"
            >
              Add More Image URL
            </b-button>
          </b-form-group>
          <div class="btn-container">
            <b-button
              type="submit"
              variant="primary"
              :disabled="isTransportMediaInvalid"
              @click="modalActiveTab = 'transportMedia'"
            >
              Update Transport & Media
            </b-button>
          </div>
        </div>
      </form>
    </b-modal>
  </div>
</template>
  
  <script>
import { kinks } from "@turf/turf";

export default {
  name: "BiocharApplication",
  data() {
    return {
      currentPage: 1,
      // Added separate active tab state for modal and main view
      mainActiveTab: "farmDetails",
      modalActiveTab: "farmDetails",
      // Split fields into logical groups
      farmDetailsFields: [
        { key: "ownerName", label: "Owner Name" },
        { key: "farmSize", label: "Farm Size (ha)" },
        { key: "crop", label: "Crop" },
        { key: "officerName", label: "Officer Name" },
        { key: "officerEmail", label: "Officer Email" },
        { key: "contactInfo", label: "Contact Info" },
        { key: "latitude", label: "Latitude" },
        { key: "longitude", label: "Longitude" },
        { key: "actions", label: "Action" },
      ],
      applicationDetailsFields: [
        { key: "ownerName", label: "Owner Name" },
        {
          key: "startDate",
          label: "Start Date",
          formatter: (value) => new Date(value).toISOString().split("T")[0],
        },
        {
          key: "endDate",
          label: "End Date",
          formatter: (value) => new Date(value).toISOString().split("T")[0],
        },
        { key: "amountPerSpot", label: "Amount per Spot (kg)" },
        { key: "numberOfSpots", label: "Number of Spots" },
        { key: "amountOfBiochar", label: "Amount of Biochar (kg)" },
        { key: "actions", label: "Action" },
      ],
      transportMediaFields: [
        { key: "ownerName", label: "Owner Name" }, // Including owner name for reference
        { key: "transportVehicleType", label: "Transport Vehicle" },
        { key: "imageUrls", label: "Image URLs" },
        { key: "actions", label: "Action" },
      ],
      transportOptions: [
        { value: null, text: "Please select an option", disabled: true },
        { value: "Diesel", text: "Diesel" },
        { value: "Petrol", text: "Petrol" },
        { value: "CNG", text: "CNG" },
        { value: "Truck", text: "Truck" },
      ],
      items: [],
      editBiocharApplication: {},
    };
  },
  mounted() {
    this.getBiocharApplications();
  },
  computed: {
    rows() {
      return this.items.length;
    },
    isFarmDetailsInvalid() {
      return (
        !this.editBiocharApplication.ownerName?.trim() ||
        !this.editBiocharApplication.farmSize ||
        !this.editBiocharApplication.crop?.trim() ||
        !this.editBiocharApplication.officerName?.trim() ||
        !this.editBiocharApplication.officerEmail?.trim() ||
        !this.editBiocharApplication.contactInfo?.trim() ||
        !this.editBiocharApplication.latitude ||
        !this.editBiocharApplication.longitude
      );
    },
    isApplicationDetailsInvalid() {
      return (
        !this.editBiocharApplication.startDate ||
        !this.editBiocharApplication.endDate ||
        !this.editBiocharApplication.amountPerSpot ||
        !this.editBiocharApplication.numberOfSpots ||
        !this.editBiocharApplication.amountOfBiochar
      );
    },
    isTransportMediaInvalid() {
      return (
        !this.editBiocharApplication.transportVehicleType ||
        !this.editBiocharApplication.imageUrls.some((url) => url.trim())
      );
    },
  },
  methods: {
    getAriaControlsId() {
      // Return the appropriate table ID based on active tab
      switch (this.mainActiveTab) {
        case "farmDetails":
          return "biochar-farm-table";
        case "applicationDetails":
          return "biochar-application-table";
        case "transportMedia":
          return "biochar-transport-table";
        default:
          return "biochar-farm-table";
      }
    },
    async getBiocharApplications() {
      try {
        const response = await this.$store.dispatch(
          "markers/fetchBiocharApplications"
        );
        this.items = response.features.map((feature) => ({
          id: feature.properties.id,
          ownerName: feature.properties.ownerName,
          farmSize: feature.properties.farmSize,
          crop: feature.properties.crop,
          officerName: feature.properties.officerName,
          officerEmail: feature.properties.officerEmail,
          contactInfo: feature.properties.contactInfo,
          startDate: feature.properties.startDate,
          endDate: feature.properties.endDate,
          amountPerSpot: feature.properties.amountPerSpot,
          numberOfSpots: feature.properties.numberOfSpots,
          amountOfBiochar: feature.properties.amountOfBiochar,
          transportVehicleType: feature.properties.transportVehicleType,
          imageUrls: feature.properties.imageUrls,
          latitude: feature.properties.latitude,
          longitude: feature.properties.longitude,
        }));
      } catch (error) {
        console.error(
          "Error fetching biochar applications:",
          error.message,
          error.stack
        );
        this.$toastr.e("Failed to fetch biochar applications");
      }
    },
    async openEditModal(id) {
      try {
        const application = await this.$store.dispatch(
          "markers/fetchSingleBiocharApplication",
          id
        );
        this.editBiocharApplication = {
          id: application._id || "",
          ownerName: application.ownerName || "",
          farmSize: application.farmSize || 0,
          crop: application.crop || "",
          officerName: application.officerName || "",
          officerEmail: application.officerEmail || "",
          contactInfo: application.contactInfo || "",
          startDate: application.startDate
            ? new Date(application.startDate).toISOString().split("T")[0]
            : "",
          endDate: application.endDate
            ? new Date(application.endDate).toISOString().split("T")[0]
            : "",
          amountPerSpot: application.amountPerSpot || 0,
          numberOfSpots: application.numberOfSpots || 0,
          amountOfBiochar: application.amountOfBiochar || 0,
          transportVehicleType: application.transportVehicleType || "",
          imageUrls:
            application.imageUrls && application.imageUrls.length
              ? [...application.imageUrls]
              : [""],
          createdBy: application.createdBy || "admin",
          latitude: application.latitude || 0,
          longitude: application.longitude || 0,
        };
        this.$refs.modal.show();
      } catch (error) {
        console.error(
          "Error fetching biochar application details:",
          error.message,
          error.stack
        );
        this.$toastr.e("Failed to fetch biochar application details");
      }
    },
    validateCoordinates(input) {
      if (!input || input.trim() === "") {
        return { valid: true, coords: [] };
      }

      const pairs = input
        .split(";")
        .map((pair) => pair.trim())
        .filter((pair) => pair);

      if (pairs.length < 4) {
        return {
          valid: false,
          error: "At least 4 coordinate pairs are required to form a polygon",
        };
      }

      const coords = [];
      for (const pair of pairs) {
        const [latStr, lonStr] = pair.split(",").map((val) => val.trim());

        if (!latStr || !lonStr) {
          return {
            valid: false,
            error: `Invalid format in pair "${pair}". Expected "lat,lon"`,
          };
        }

        const lat = parseFloat(latStr);
        const lon = parseFloat(lonStr);

        if (isNaN(lat) || isNaN(lon)) {
          return {
            valid: false,
            error: `Invalid number in pair "${pair}". Coordinates must be numbers`,
          };
        }

        if (lat < -90 || lat > 90) {
          return {
            valid: false,
            error: `Latitude in pair "${pair}" must be between -90 and 90`,
          };
        }
        if (lon < -180 || lon > 180) {
          return {
            valid: false,
            error: `Longitude in pair "${pair}" must be between -180 and 180`,
          };
        }

        coords.push({ lat, lon });
      }

      const coordStrings = coords.map(({ lat, lon }) => `${lon},${lat}`);
      for (let i = 0; i < coordStrings.length - 1; i++) {
        for (let j = i + 1; j < coordStrings.length - 1; j++) {
          if (coordStrings[i] === coordStrings[j]) {
            return {
              valid: false,
              error: `Duplicate coordinates found at positions ${i + 1} and ${
                j + 1
              }`,
            };
          }
        }
      }

      if (coordStrings[0] !== coordStrings[coordStrings.length - 1]) {
        return {
          valid: false,
          error:
            "Polygon must be closed: the last coordinate must match the first",
        };
      }

      const polygonCoords = coords.map(({ lat, lon }) => [lon, lat]);
      const geojson = {
        type: "Polygon",
        coordinates: [polygonCoords],
      };
      const kinksResult = kinks(geojson);
      if (kinksResult.features.length > 0) {
        return {
          valid: false,
          error: "Polygon is invalid: edges cross (self-intersecting polygon)",
        };
      }

      return { valid: true, coords };
    },
    async updateBiocharApplication() {
      try {

        const payload = {
          ownerName: String(this.editBiocharApplication.ownerName || ""),
          farmSize: Number(this.editBiocharApplication.farmSize) || 0,
          crop: String(this.editBiocharApplication.crop || ""),
          officerName: String(this.editBiocharApplication.officerName || ""),
          officerEmail: String(this.editBiocharApplication.officerEmail || ""),
          contactInfo: String(this.editBiocharApplication.contactInfo || ""),
          startDate: String(this.editBiocharApplication.startDate || ""),
          endDate: String(this.editBiocharApplication.endDate || ""),
          amountPerSpot: Number(this.editBiocharApplication.amountPerSpot) || 0,
          numberOfSpots: Number(this.editBiocharApplication.numberOfSpots) || 0,
          amountOfBiochar:
            Number(this.editBiocharApplication.amountOfBiochar) || 0,
          transportVehicleType: String(
            this.editBiocharApplication.transportVehicleType || ""
          ),
          imageUrls: this.editBiocharApplication.imageUrls
            .filter((url) => url.trim() !== "")
            .map((url) => url.trim()),
          createdBy: String(this.editBiocharApplication.createdBy || "admin"),
          latitude: Number(this.editBiocharApplication.latitude) || 0,
          longitude: Number(this.editBiocharApplication.longitude) || 0,
        };

        // Validate fields based on active tab
        if (this.modalActiveTab === "farmDetails") {
          if (this.isFarmDetailsInvalid) {
            this.$toastr.e("Please fill all required fields in Farm Details");
            return;
          }
        } else if (this.modalActiveTab === "applicationDetails") {
          if (this.isApplicationDetailsInvalid) {
            this.$toastr.e(
              "Please fill all required fields in Application Details"
            );
            return;
          }
        } else if (this.modalActiveTab === "transportMedia") {
          if (this.isTransportMediaInvalid) {
            this.$toastr.e(
              "Please fill all required fields in Transport & Media"
            );
            return;
          }
        }

        const res = await this.$store.dispatch(
          "markers/updateBiocharApplication",
          {
            id: this.editBiocharApplication.id,
            ...payload,
          }
        );
        if (res.status === 200) {
          this.$toastr.s("Biochar Application successfully updated");
          await this.getBiocharApplications();
          this.$refs.modal.hide();
        }
      } catch (error) {
        console.error(
          "Error updating biochar application:",
          error.message,
          error.stack
        );
        this.$toastr.e("Failed to update biochar application");
      }
    },
    addImageUrl() {
      this.editBiocharApplication.imageUrls.push("");
    },
    removeImageUrl(index) {
      this.editBiocharApplication.imageUrls.splice(index, 1);
      if (this.editBiocharApplication.imageUrls.length === 0) {
        this.editBiocharApplication.imageUrls.push("");
      }
    },
    truncateUrl(url) {
      return url.length > 30 ? url.substring(0, 27) + "..." : url;
    },
    resetEditBiocharApplication() {
      this.editBiocharApplication = {};
      this.modalActiveTab = "farmDetails";
    },
    async showDeletePrompt(itemId) {
      try {
        const isDeleteConfirmed = await this.$bvModal.msgBoxConfirm(
          "Please confirm that you want to delete this Biochar Application.",
          {
            size: "md",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: "YES",
            cancelTitle: "NO",
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true,
          }
        );
        if (!isDeleteConfirmed) return;
        const res = await this.$store.dispatch(
          "markers/deleteBiocharApplication",
          itemId
        );
        if (res.data) {
          this.$toastr.s("Biochar Application successfully deleted");
          this.items = this.items.filter((app) => app.id !== itemId);
        }
      } catch (error) {
        console.error(
          "Error deleting biochar application:",
          error.message,
          error.stack
        );
        this.$toastr.e("Failed to delete biochar application");
      }
    },
  },
};
</script>
  
  <style scoped>
.table-container {
  overflow-x: auto;
  width: 100%;
  margin-top: 1rem;
}

.image-url-link {
  color: #007bff;
  text-decoration: none;
}

.image-url-link:hover {
  text-decoration: underline;
}

.tabs {
  display: flex;
  margin-bottom: 1rem;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

.tab {
  flex: 1;
  padding: 0.8rem;
  background: #f5f5f5;
  border: none;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  border-right: 1px solid #e0e0e0;
}

.tab:last-child {
  border-right: none;
}

.tab-active {
  background-color: #4caf50;
  color: white;
}

.tab-content {
  padding: 1rem;
  background: #fff;
  border-radius: 5px;
}

.btn-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.url-input-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cancel-btn {
  min-width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  line-height: 1;
}

.action-btn {
  margin-top: 0.5rem;
}

.add-url-btn {
  background-color: #28a745;
  border-color: #28a745;
}

.add-url-btn:hover {
  background-color: #218838;
  border-color: #1e7e34;
}

/* Pagination styling */
.pagination {
  margin-top: 1.5rem;
  justify-content: center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .tabs {
    flex-direction: column;
  }

  .tab {
    border-right: none;
    border-bottom: 1px solid #e0e0e0;
  }

  .tab:last-child {
    border-bottom: none;
  }

  .url-input-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .cancel-btn {
    margin-left: auto;
  }
}
</style>